/* =========================================================================
   TT_GARMENT_FRAME_START   —   APPROVED PRODUCTION-INTENT ARCHITECTURE

   This is the garment-relative frame the V2 renderer is built on, and it
   IS intended for production. The earlier "DEV / TEST BUILD ONLY - do not
   port verbatim" warning was retired on 2026-09-02: it predated approval
   and, taken literally, forbade the cutover this file exists to enable.

   *** LOCKED. The composite fit / zoom behaviour, the crop mathematics,
   *** SAFETY_MARGIN and the display clamp below were visually approved on
   *** PC78H and NL6211 and must NOT be changed without Jeffrey's explicit
   *** authorisation. Read the clamp note before touching that line.

   WHAT THIS DOES
   Two jobs, deliberately kept separate:

   1. GARMENT FRAME
      Wraps #ttV2PreviewImg in #ttV2Garment, an element whose box is exactly
      the rendered garment rectangle (via aspect-ratio from the source image's
      intrinsic dimensions). All overlay boxes are moved inside it, so their
      percentage coordinates resolve against the GARMENT, not the grey stage.
      This is the permanent architecture. In production it should be baked
      into sections/tt-mockup-preview-v2.liquid as real markup rather than
      assembled here in JS.

   2. TRANSLATION LAYER  ** TEMPORARY **
      mock.placement_json is shared across every theme, so the live theme must
      keep receiving the existing stage-relative numbers untouched. This layer
      converts those legacy coordinates to garment-relative ones at render
      time, using the closed-form transform from the Aug 28 audit.

      Once the metafields are converted for real, DELETE the translate()
      function and the calls to it. The frame in job 1 stays.

   LEGACY REFERENCE FRAME (what the old numbers were authored against)
      .tt-v2-wrap  max-width 1200, padding 0 20   -> 1160
      .tt-v2-card  padding 18                     -> stage padding box 1124
      .tt-v2-imgwrap padding 36, border-box       -> content box 1052
      #ttV2PreviewImg width/height 100%, max 900 x 760, object-fit contain

      Every base image in the catalogue is taller than 1.184:1, so the image
      ELEMENT always saturated at 900 x 760 and the stage padding box was a
      constant 1124 x 832 for any viewport >= ~1240px. That constancy is the
      only reason the old values are invertible at all.

      *** THESE CONSTANTS DESCRIBE THE PAST. They are frozen inputs to the
      *** translation maths and must NEVER be updated to match the display
      *** sizing below. Changing them would silently move every logo.

   ---- DIMENSION SOURCE — OPTION B, approved 2026-08-29 -----------------
   The frame needs each base image's true pixel size to build a box the
   shape of the garment photo. That size used to be expected from a
   TT_BASE_DIMS_V2 script tag printed by the section — which was never
   actually added, so dims stayed null, --tt-ar fell back to 1 (SQUARE)
   and translate() silently passed legacy coordinates through untouched.
   DEV therefore did not agree with live.

   Option B reads the size from the loaded <img> instead
   (naturalWidth / naturalHeight). No Liquid change, no section edit, and
   it works for the irregular legacy images and standardised ones alike
   because nothing about the image is assumed.

   GRACEFUL DEGRADATION — deliberate:
     The frame is NOT built until a real measurement succeeds. Until then
     no CSS is injected and no wrapper exists, so the page renders exactly
     as the live theme does. If an image never loads, or reports zero
     dimensions, the frame simply never turns on for that view and the
     section's own stage-relative rendering remains in force. There is no
     state in which a garment box exists without a measured ratio.

   Retire this whole mechanism when every base image is one fixed canvas:
   the ratio then becomes a constant and needs no measuring at all.
   -----------------------------------------------------------------------

   ---- DISPLAY SIZING — OPTION A, approved 2026-08-28 --------------------
   History of this one line:

     760px fixed              too small, and ignored the window entirely
     calc(100vh - 190px)      too aggressive. A FIXED reserve means the
                              garment keeps 82-87% of viewport height at
                              every screen size, so it was structurally
                              guaranteed to eat the screen.
     clamp(800px, 66vh, 1050px)   superseded 2026-08-31. Sound in
                              principle, but the 800px FLOOR pinned every
                              common desktop: at ~900px inner height 66vh
                              is ~594px, so the floor always won and the
                              vh term never engaged. Effectively a fixed
                              800px, which left the garment at only ~727px
                              wide inside a 1084px stage - a third of the
                              horizontal budget unspent.
     clamp(1000px, 80vh, 1100px)  superseded 2026-08-31, same day.
                              Right scale, wrong fit. Its size was judged
                              as a FRACTION OF VIEWPORT HEIGHT (~89%),
                              but the garment box does not start at the
                              top of the viewport: roughly 300px of site
                              header, nav, title, meta and padding sit
                              above it. The hem was therefore already
                              below the fold at 800px too - this value
                              only made an existing overflow obvious.
     clamp(560px, calc(100vh - 160px), 1100px)  <- CURRENT AND IN FORCE.
                              Approved
                              2026-08-31 on empirical evidence, not
                              arithmetic: at 90% browser zoom the whole
                              garment fit almost perfectly and the
                              presentation looked right. Zoom shrinks the
                              CHROME as well as the garment, and at 90%
                              the garment is only ~1% smaller in screen
                              pixels. So the ~1000px scale was never the
                              problem; the fixed chrome above the stage
                              was. Subtracting it once reproduces that
                              view at 100% zoom.

     * calc(100vh - 160px)
                      the operative term at every common desktop size.
                      The chrome above the stage is a FIXED pixel
                      quantity, not a proportional one, so a fixed
                      reserve is the correct shape: subtract it once and
                      the garment bottom tracks the fold at every window
                      height. 160 is the value IN FORCE and the one
                      visually approved on PC78H and NL6211 with the
                      composite fit / zoom active. An earlier 320 figure
                      appeared in this comment; it predated the crop,
                      was never the value in the code, and is SUPERSEDED.
                      Do not "restore" it - doing so would change the
                      approved presentation.
                      THIS IS THE ONLY TUNING KNOB. Hem still clipped ->
                      raise it. Dead white space below the garment ->
                      lower it. Do not reach for the floor or ceiling.
     * 560px floor    legibility guard for very short windows; engages
                      below ~880px of viewport height. It does NOT
                      guarantee a fit - on a window that short, nothing
                      does.
     * 1100px ceiling NOT arbitrary. The served base canvas is 2200px tall,
                      so at DPR 2 a 1100px frame is exactly 1:1. Above it
                      the browser upsamples the garment photo. Rev 5 3.4
                      sized the flat-image upscale budget against this same
                      limit. Raise the ceiling only with a resolution
                      decision to match.

   Below ~1025px of viewport width max-width:100% binds before the height
   does, so the garment is width-driven there and this line is inert. The
   change only reaches viewports wide enough to have had white space.

   max-width:100% still applies, so on wide-but-short garments the width
   budget can bind first — aspect-ratio then re-derives the height and the
   proportions are preserved. Nothing crops, nothing stretches.

   THIS IS PURELY PRESENTATIONAL. Every overlay is a percentage child of
   the garment, so all four boxes scale with it automatically and the
   artwork inside them keeps object-fit:contain. No placement value, no
   metafield, no artwork sizing rule and no DEFAULT_BOX is involved.
   -----------------------------------------------------------------------

   ---- COMPOSITE FIT / ZOOM — added 2026-09-02 --------------------------
   THE PROBLEM IT SOLVES. The V2 flat standard is a 2000 x 2200 canvas and
   the garment does not fill it. PC78H front ink occupies 68.5% of the
   width and 87.5% of the height; the back is lower still. Because the
   frame is sized to the CANVAS - which it must be, since every placement
   percentage resolves against it - roughly a third of the displayed width
   was transparent padding. Enlarging the frame scaled the padding too, so
   display sizing could never close that gap.

   THE FIX. #ttV2Garment stays EXACTLY the full 2000 x 2200 canvas box and
   is never resized relative to that canvas. A new #ttV2GarmentView wraps
   it, carries the height clamp, and clips. The canvas is then made larger
   than its window and offset so the measured ink region is what shows:

       view aspect      = (canvasW * fw) : (canvasH * fh)
       #ttV2Garment w   = 100% / fw          h = 100% / fh
       #ttV2Garment left= -100% * fx / fw    top = -100% * fy / fh

   Percentages inside #ttV2Garment still resolve against the whole canvas,
   so NO placement coordinate, garment landmark or anchor changes. Nothing
   here is a transform: scale(), deliberately - a transform would create a
   containing block and interact with the overlays' own translate(-50%).

   WHERE THE BOUNDS COME FROM. TTPlacement.measureInk() - the same measured
   ink capability the renderer already uses for artwork. It is generic: it
   takes any URL and returns canvas fractions. Nothing is hardcoded and no
   PNG is modified. Bounds are unioned across views so Front and Rear keep
   the SAME apparent scale, then padded by SAFETY_MARGIN so artwork sitting
   near or slightly outside the silhouette is never clipped.

   The union only ever GROWS. If a later view needs more room the crop
   widens; it never tightens, so the garment cannot shrink mid-session.

   ** KNOWN LIMIT ** Only the CURRENTLY DISPLAYED base URL is reachable
   from this file - the front/back/sleeve URLs live in a closure inside
   sections/tt-mockup-preview-v2.liquid, which is out of scope. The union
   is therefore built incrementally as views are visited. For PC78H the
   back ink falls entirely inside the front-derived crop, so visiting Rear
   changes nothing. A garment whose back is wider than its front would
   widen the crop once, on first visit to Rear.

   FAIL-SAFE. If TTPlacement is absent, if measurement fails, or if the
   canvas is tainted, measureInk returns FULL_INK (fx0 fy0 fw1 fh1). Every
   expression above then collapses to width:100% height:100% left:0 top:0
   and the view aspect falls back to the canvas aspect - i.e. EXACTLY the
   uncropped rendering that preceded this change. The CSS defaults carry
   the same values, so a crop is only ever applied on a real measurement.

   RESOLUTION BUDGET. The 1100px ceiling is a property of the INNER canvas,
   not the window. Since the canvas is now taller than its view, the view
   carries max-height: calc(1100px * fh) so the inner 2000 x 2200 canvas
   still stops at 1:1 for DPR 2 and never upsamples. With fh defaulting to
   1 this is exactly the original 1100px ceiling.
   -----------------------------------------------------------------------
   ========================================================================= */
(function () {
  'use strict';

  if (window.__TT_GARMENT_FRAME_BOUND__) return;
  window.__TT_GARMENT_FRAME_BOUND__ = true;

  /* --- legacy stage constants. Do not "tidy" these numbers. ------------- */
  var LEGACY = {
    STAGE_W: 1124,
    STAGE_H: 832,
    ELEM_W: 900,
    ELEM_H: 760,
    ELEM_LEFT: 112,
    ELEM_TOP: 36
  };

  var dims = null;        /* optional seed from TT_BASE_DIMS_V2 (normally absent) */
  var measured = {};      /* { front:{w,h}, ... } read from the loaded <img> */
  var frameActive = false;/* true only once a real measurement has succeeded */
  var root = null;
  var garment = null;

  /* ---- COMPOSITE FIT / ZOOM state -------------------------------------- */
  /* Fraction of the canvas added on EVERY side of the measured union before
     clipping. Guards artwork that sits on or just outside the silhouette -
     a cuff logo, a wide back print. 0.02 = 40px of 2000, 44px of 2200. This
     is the only tuning knob for the crop; raise it if anything clips. */
  var SAFETY_MARGIN = 0.02;
  var DPR_CEILING_PX = 1100;   /* inner canvas 1:1 at DPR 2 on a 2200 canvas */

  var view_el = null;          /* #ttV2GarmentView - the clipping window */
  var inkByView = {};          /* view -> ink fractions from measureInk() */
  var crop = null;             /* committed union+margin, or null = no crop */

  /* Union of every view measured so far, padded by SAFETY_MARGIN and
     clamped to the canvas. Returns null while nothing has been measured,
     which leaves the CSS defaults - i.e. the full uncropped canvas. */
  function unionCrop() {
    var l = 1, t = 1, r = 0, b = 0, any = false;
    for (var v in inkByView) {
      if (!Object.prototype.hasOwnProperty.call(inkByView, v)) continue;
      var i = inkByView[v];
      if (!i || !i.measured) continue;
      any = true;
      if (i.fx < l) l = i.fx;
      if (i.fy < t) t = i.fy;
      if (i.fx + i.fw > r) r = i.fx + i.fw;
      if (i.fy + i.fh > b) b = i.fy + i.fh;
    }
    if (!any) return null;

    l = Math.max(0, l - SAFETY_MARGIN);
    t = Math.max(0, t - SAFETY_MARGIN);
    r = Math.min(1, r + SAFETY_MARGIN);
    b = Math.min(1, b + SAFETY_MARGIN);

    var fw = r - l, fh = b - t;
    /* Implausibly small result means the measurement is not trustworthy.
       Bail rather than zoom into a sliver. */
    if (!(fw > 0.2) || !(fh > 0.2)) return null;
    return { fx: l, fy: t, fw: fw, fh: fh };
  }

  function cropCovers(c, n) {
    var e = 1e-6;
    return n.fx >= c.fx - e && n.fy >= c.fy - e &&
           (n.fx + n.fw) <= (c.fx + c.fw) + e &&
           (n.fy + n.fh) <= (c.fy + c.fh) + e;
  }

  /* Commit the crop to CSS variables. Only ever widens, so the garment
     cannot shrink when a later view is measured. */
  function setCrop() {
    if (!view_el) return;
    var next = unionCrop();
    if (!next) return;
    if (crop && cropCovers(crop, next)) return;

    crop = next;
    view_el.style.setProperty('--tt-ink-fx', crop.fx);
    view_el.style.setProperty('--tt-ink-fy', crop.fy);
    view_el.style.setProperty('--tt-ink-fw', crop.fw);
    view_el.style.setProperty('--tt-ink-fh', crop.fh);
    setRatio(currentView());
  }

  /* Measure a base image's visible ink through the SHARED module. Never
     hardcodes bounds and never touches the file. A failed or unmeasurable
     image is simply not recorded, leaving the full canvas in place. */
  function measureBase(view, url) {
    if (!url || !view) return;
    if (inkByView[view]) return;
    if (!window.TTPlacement || !window.TTPlacement.measureInk) return;

    window.TTPlacement.measureInk(url).then(function (ink) {
      if (!ink || !ink.measured) return;   /* fail-safe: stay uncropped */
      inkByView[view] = ink;
      setCrop();
    });
  }

  function noteBase() {
    var img = document.getElementById('ttV2PreviewImg');
    if (!img) return;
    measureBase(currentView(), img.currentSrc || img.getAttribute('src') || '');
  }

  /* ------------------------------------------------------------- utils */
  function readJsonScript(id, fallback) {
    var el = document.getElementById(id);
    if (!el) return fallback;
    try {
      var raw = (el.textContent || '').trim();
      if (!raw) return fallback;
      var parsed = JSON.parse(raw);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      return parsed;
    } catch (e) {
      return fallback;
    }
  }

  function currentView() {
    var active = document.querySelector('[data-tt-v2-view].is-active');
    return active ? active.getAttribute('data-tt-v2-view') : 'front';
  }

  /* ------------------------------------------------------ TT_GF_MEASURE
     OPTION B. Read the base image's intrinsic size straight off the
     element. Returns null unless the browser genuinely has usable numbers,
     so a broken or still-loading image can never produce a bogus ratio. */
  function readIntrinsic() {
    var img = document.getElementById('ttV2PreviewImg');
    if (!img) return null;
    if (!img.complete) return null;
    var w = Number(img.naturalWidth);
    var h = Number(img.naturalHeight);
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
    return { w: w, h: h };
  }

  function measureCurrent() {
    var d = readIntrinsic();
    if (!d) return null;
    measured[currentView()] = d;
    return d;
  }

  function dimsFor(view) {
    var d = (measured && measured[view]) || (dims && dims[view]);
    if (!d) return null;
    var w = Number(d.w), h = Number(d.h);
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return null;
    return { w: w, h: h };
  }

  /* ------------------------------------------- TT_GF_LEGACY_GARMENT_RECT
     Where the garment actually sat inside the old 1124 x 832 stage.
     Uses LEGACY only — never the current on-screen size. */
  function legacyRect(iw, ih) {
    var s = Math.min(LEGACY.ELEM_W / iw, LEGACY.ELEM_H / ih);
    var wg = iw * s;
    var hg = ih * s;
    return {
      left: LEGACY.ELEM_LEFT + (LEGACY.ELEM_W - wg) / 2,
      top: LEGACY.ELEM_TOP + (LEGACY.ELEM_H - hg) / 2,
      w: wg,
      h: hg
    };
  }

  /* --------------------------------------------------- TT_GF_TRANSLATE
     stage-relative %  ->  garment-relative %.   TEMPORARY.
     Output is a ratio, so it is independent of how large the garment is
     drawn. Resizing the display cannot move a logo. */
  function translate(cfg, view) {
    if (!cfg || typeof cfg !== 'object') return cfg;

    var d = dimsFor(view);
    if (!d) return cfg;                 /* no dimensions -> pass through */

    var r = legacyRect(d.w, d.h);
    var out = {};
    for (var k in cfg) {
      if (Object.prototype.hasOwnProperty.call(cfg, k)) out[k] = cfg[k];
    }

    if (typeof cfg.x !== 'undefined') {
      out.x = (Number(cfg.x) / 100 * LEGACY.STAGE_W - r.left) / r.w * 100;
    }
    if (typeof cfg.y !== 'undefined') {
      out.y = (Number(cfg.y) / 100 * LEGACY.STAGE_H - r.top) / r.h * 100;
    }
    if (typeof cfg.w !== 'undefined') {
      out.w = (Number(cfg.w) / 100 * LEGACY.STAGE_W) / r.w * 100;
    }
    if (typeof cfg.h !== 'undefined') {
      out.h = (Number(cfg.h) / 100 * LEGACY.STAGE_H) / r.h * 100;
    }
    return out;
  }

  /* --------------------------------------------------------- TT_GF_CSS */
  function injectCss() {
    if (document.getElementById('ttV2GarmentCss')) return;

    var css = [
      /* Stage is decorative now — no geometry depends on it. Trimming its
         padding hands the space straight to the garment. */
      '.tt-v2-imgwrap{',
      '  padding:20px !important;',
      '  min-height:0 !important;',
      '}',
      /* The clipping window. Carries the display clamp (unchanged) and the
         DPR ceiling, which applies to the INNER canvas, hence * fh. Every
         default below is the no-crop identity, so an unmeasured garment
         renders exactly as it did before this file gained the viewport. */
      '.tt-v2-garment-view{',
      '  position:relative;',
      '  aspect-ratio:var(--tt-view-ar, var(--tt-ar, 1));',
      '  height:clamp(560px, calc(100vh - 160px), 1100px);',
      '  max-height:calc(' + DPR_CEILING_PX + 'px * var(--tt-ink-fh, 1));',
      '  width:auto;',
      '  max-width:100%;',
      '  margin:0 auto;',
      '  flex:0 0 auto;',
      '  overflow:hidden;',
      '  outline:calc(var(--tt-debug) * 2px) solid #d000d0;',
      '}',
      /* STILL THE COMPLETE 2000 x 2200 CANVAS. Larger than its window and
         offset, so the measured ink shows. Placement percentages resolve
         against this box and are therefore untouched. */
      '.tt-v2-garment{',
      '  position:absolute;',
      '  width:calc(100% / var(--tt-ink-fw, 1));',
      '  height:calc(100% / var(--tt-ink-fh, 1));',
      '  left:calc(-100% * var(--tt-ink-fx, 0) / var(--tt-ink-fw, 1));',
      '  top:calc(-100% * var(--tt-ink-fy, 0) / var(--tt-ink-fh, 1));',
      '  max-width:none;',
      '  margin:0;',
      '  outline:calc(var(--tt-debug) * 2px) dashed #00a000;',
      '}',
      '.tt-v2-garment > #ttV2PreviewImg{',
      '  width:100% !important;',
      '  height:100% !important;',
      '  max-width:none !important;',
      '  max-height:none !important;',
      '  margin:0 !important;',
      '  object-fit:contain;',
      '  display:block;',
      '}',
      '@media (max-width:749px){',
      '  .tt-v2-imgwrap{ padding:12px !important; }',
      '  .tt-v2-garment-view{ height:auto; width:100%; max-height:78vh; }',
      '}'
    ].join('\n');

    var style = document.createElement('style');
    style.id = 'ttV2GarmentCss';
    style.textContent = css;
    document.head.appendChild(style);
  }

  /* ------------------------------------------------------- TT_GF_BUILD */
  function build() {
    root = document.getElementById('ttV2Root');
    if (!root) return false;

    var img = document.getElementById('ttV2PreviewImg');
    if (!img) return false;

    var existing = document.getElementById('ttV2Garment');
    if (existing) {
      garment = existing;
      view_el = document.getElementById('ttV2GarmentView');
      return true;
    }

    /* The clipping window sits where the garment used to, and the canvas
       box moves inside it. The img and every overlay stay children of
       #ttV2Garment, so their percentage coordinates are unaffected. */
    view_el = document.createElement('div');
    view_el.id = 'ttV2GarmentView';
    view_el.className = 'tt-v2-garment-view';
    view_el.setAttribute('data-tt-frame', 'view');

    garment = document.createElement('div');
    garment.id = 'ttV2Garment';
    garment.className = 'tt-v2-garment';
    garment.setAttribute('data-tt-frame', 'garment');

    img.parentNode.insertBefore(view_el, img);
    view_el.appendChild(garment);
    garment.appendChild(img);

    /* Overlays become children of the garment, not the stage. */
    ['ttV2LogoBoxChest', 'ttV2LogoBoxBack', 'ttV2LogoBoxSleeve', 'ttV2NameBox']
      .forEach(function (id) {
        var el = document.getElementById(id);
        if (el) garment.appendChild(el);
      });

    return true;
  }

  /* ---------------------------------------------------- TT_GF_SET_RATIO */
  function setRatio(view) {
    if (!garment) return;
    var d = dimsFor(view);
    if (!d) return;
    garment.style.setProperty('--tt-ar', d.w + ' / ' + d.h);

    /* The window's shape is the CROPPED canvas. With no crop committed
       this is the canvas ratio, identical to the previous behaviour. */
    if (view_el) {
      var c = crop || { fw: 1, fh: 1 };
      view_el.style.setProperty('--tt-view-ar',
        (d.w * c.fw) + ' / ' + (d.h * c.fh));
    }
  }

  /* ---------------------------------------------------- TT_GF_APPLY_BOX */
  function applyBox(id, cfg, defaultTransform) {
    var el = document.getElementById(id);
    if (!el || !cfg || typeof cfg !== 'object') return;

    if (typeof cfg.x !== 'undefined') el.style.left = cfg.x + '%';
    if (typeof cfg.y !== 'undefined') el.style.top = cfg.y + '%';
    if (typeof cfg.w !== 'undefined') el.style.width = cfg.w + '%';
    if (typeof cfg.h !== 'undefined') el.style.height = cfg.h + '%';

    if (cfg.anchor === 'top') el.style.transform = 'translate(-50%, 0)';
    else if (cfg.anchor === 'center') el.style.transform = 'translateX(-50%)';
    else if (cfg.anchor === 'none') el.style.transform = 'none';
    else if (defaultTransform) el.style.transform = defaultTransform;
  }

  /* --------------------------------------------- TT_GF_ZONE_OWNERSHIP
     ADDED 2026-08-31 for the renderer port. Where the ported renderer in
     sections/tt-mockup-preview-v2.liquid drives a zone from
     mock.placement_json_v2 through tt-placement.js, this file must not
     overwrite it with a translated v1 value. Everything else is unchanged:
     the frame still builds the wrapper, sets the aspect ratio, and keeps
     translating the zones it still owns (sleeve, plus full front via
     TTGarmentFrame.translate). If the renderer never initialises the flag
     is never set and the previous behaviour returns untouched. */
  function rendererOwns(zone) {
    var o = window.TT_V2_RENDERER_ZONES;
    return !!(o && o[zone]);
  }

  /* -------------------------------------------------------- TT_GF_APPLY
     Runs AFTER the section's own applyPlacementJson() and overwrites it
     with translated values. Same box ids, same anchor semantics. */
  function apply() {
    var view = currentView();
    setRatio(view);

    var p = readJsonScript('TT_PLACEMENT_JSON_V2', null);
    if (!p || typeof p !== 'object') return;

    if (p.front && p.front.logo_chest && !rendererOwns('logo_chest')) {
      applyBox('ttV2LogoBoxChest', translate(p.front.logo_chest, 'front'), 'translateX(-50%)');
    }

    if (p.back && p.back.logo_back && !rendererOwns('logo_back')) {
      var backCfg = translate(p.back.logo_back, 'back');
      backCfg.anchor = 'top';
      applyBox('ttV2LogoBoxBack', backCfg, 'translate(-50%, 0)');
    }

    if (p.sleeve && p.sleeve.logo_sleeve) {
      applyBox('ttV2LogoBoxSleeve', translate(p.sleeve.logo_sleeve, 'sleeve'), 'none');
    }

    var nameCfg = null;
    if (p.front && p.front.name) nameCfg = p.front.name;
    else if (p.front_name) nameCfg = p.front_name;
    else if (p.name) nameCfg = p.name;

    if (nameCfg && !rendererOwns('name')) {
      applyBox('ttV2NameBox', translate(nameCfg, 'front'), 'none');
    }
  }

  /* ----------------------------------------------------- TT_GF_ACTIVATE
     Builds the frame the first time a measurement succeeds. Before that
     the page is untouched and renders exactly as live does. */
  function activate() {
    if (frameActive) return true;

    injectCss();
    if (!build()) return false;
    frameActive = true;

    /* ?tt_debug=1 turns on the existing --tt-debug outlines */
    try {
      if (new URL(window.location.href).searchParams.get('tt_debug') === '1') {
        document.documentElement.style.setProperty('--tt-debug', '1');
      }
    } catch (e) {}

    return true;
  }

  /* Re-apply only when the CURRENT view has a real measurement. On a view
     switch the new image has not loaded yet, so this returns and the load
     handler below does the work a moment later. */
  function refresh() {
    if (!frameActive) return;
    if (!dimsFor(currentView())) measureCurrent();
    if (!dimsFor(currentView())) return;
    apply();
  }

  /* --------------------------------------------------------- TT_GF_INIT */
  function init() {
    dims = readJsonScript('TT_BASE_DIMS_V2', null);

    var img = document.getElementById('ttV2PreviewImg');
    if (!img) return;

    /* Fires on first paint and again on every view switch. */
    img.addEventListener('load', function () {
      if (!measureCurrent()) return;
      if (!activate()) return;
      apply();
      noteBase();
    });

    img.addEventListener('error', function () {
      console.warn('TT garment frame: base image failed to load — frame stays off for this view, stage rendering retained.');
    });

    /* A cached image can already be complete before the listener attaches. */
    if (measureCurrent() && activate()) { apply(); noteBase(); }

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-tt-v2-view]') || e.target.closest('[data-tt-ff-mode]')) {
        setTimeout(refresh, 0);
      }
    });
  }

  /* Exposed so tt-full-front.js can translate its own box through the
     identical transform without duplicating the constants. */
  window.TTGarmentFrame = {
    translate: translate,
    apply: apply,
    host: function () { return garment; },
    legacy: LEGACY
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
/* === TT_GARMENT_FRAME_END === */
